import { useCallback, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { applicationsApi } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { Job, JobApplicationState } from '../types';
import { errorMessage } from '../utils/errors';

type SaveTarget = Pick<Job, 'id' | 'application_status' | 'application_id'>;

interface ReturnTo {
  pathname: string;
  search?: string;
}

type Notify = (message: string, severity?: 'success' | 'error') => void;

/**
 * Save and un-save a job. Saving creates the user's application with status
 * `saved`; un-saving deletes it. An application that has moved past `saved`
 * is never deleted from here: that would throw away a tracked application.
 *
 * Signed-out users are sent to /login and returned to `returnTo` (the current
 * page by default) afterwards.
 */
export function useSaveJob(notify: Notify, onChange: (jobId: string, next: JobApplicationState) => void) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const navigate = useNavigate();
  const location = useLocation();
  // Read at call time so the callback keeps its identity as the URL changes.
  const locationRef = useRef(location);
  locationRef.current = location;
  const [savingIds, setSavingIds] = useState<string[]>([]);
  const inFlight = useRef<Record<string, boolean>>({});

  const toggleSave = useCallback(
    async (job: SaveTarget, returnTo?: ReturnTo) => {
      if (!isAuthenticated) {
        navigate('/login', {
          state: {
            from: returnTo ?? {
              pathname: locationRef.current.pathname,
              search: locationRef.current.search,
            },
          },
        });
        return;
      }
      const removing = job.application_status === 'saved' && Boolean(job.application_id);
      if (!removing && job.application_status) return; // already past saved
      if (inFlight.current[job.id]) return;
      inFlight.current[job.id] = true;
      setSavingIds((ids) => [...ids, job.id]);
      try {
        if (removing && job.application_id) {
          await applicationsApi.remove(job.application_id);
          onChange(job.id, { application_status: null, application_id: null, is_saved: false });
          notify('Removed from your saved jobs.');
        } else {
          const application = await applicationsApi.create(job.id);
          onChange(job.id, {
            application_status: application.status,
            application_id: application.id,
            is_saved: true,
          });
          notify('Job saved.');
        }
      } catch (e) {
        notify(errorMessage(e, removing ? 'Could not remove this job.' : 'Could not save this job.'), 'error');
      } finally {
        delete inFlight.current[job.id];
        setSavingIds((ids) => ids.filter((id) => id !== job.id));
      }
    },
    [isAuthenticated, navigate, notify, onChange]
  );

  return { toggleSave, savingIds };
}
